import { useEffect, useRef, useState } from "react";
import DoneIcon from '../../assets/icons/fbs/GreenCheck.svg';
import StepHeader from "./StepHeader";
import Checkbox from "@mui/material/Checkbox";
import PhoneInputComp from '../BaseComponents/PhoneInput';
import axios from 'axios';
import { API_V1_PREFIX, isProd } from '../../utils/constants';
import { toast } from 'react-toastify';
import { fireCheckoutDetailsSubmitted } from '../../utils/dataLayer';

const submitLeadToRhForm = (data: { name: string; email: string; phone: string }) => {
    const rhForm = (window as any).RH_MF70bd716e94;
    if (rhForm?.pendingReferral) {
        rhForm.pendingReferral(data);
    }
};

const InputField = ({ title, type, placeholder, name, value, onChange, error, max, labelTop }: { 
    title: string, type: string, placeholder?: string, name: string, value: string, 
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, error?: string, max?: string, labelTop?: boolean
}) => {
    const [focused, setFocused] = useState(false);
    return (
        <div className={error ? 'error' : ''}>
            <div className={`input-field-wrapper  ${(value.length > 0 || placeholder != null || labelTop) ? 'filled' : ''} ${focused ? 'focused' : ''}`} >
                <input 
                type={type} 
                placeholder={placeholder}
                className="input-field" 
                name={name}
                value={value} 
                max={max}
                onChange={onChange}
                onFocus={() => setFocused(true)} 
                onBlur={() => setFocused(false)} />
                <label className="input-field-title" >{title}</label>
                {value.length > 0 && !error && <img src={DoneIcon} alt="Done" className="done-icon" width={18} height={18} />}
            </div>
            {error && <div className="error-text-message" >{error}</div>}
        </div>
    )
}

const RadioGroupComponent = ({ title, options, required, checked, name, onChange }: { 
    title: string, options: { label: string; value: string }[], required?: boolean, 
    checked?: string, name: string, onChange?: (value: string) => void 
}) => {
    const [selectedValue, setSelectedValue] = useState<string | undefined>(checked);

    const handleChange = (value: string) => {
        setSelectedValue(value);
        onChange?.(value);
    };

    return (
        <div className="gray-card-wrapper" >
            <div className="radio-group-title" >{title}{required && <span className="required-asterisk" >*</span>}</div>
            <div className="radio-group-options" >
                {options.map((option) => {
                    const optionId = `${name}-${option.value}`;
                    return (
                    <div key={optionId} className="radio-group-option" >
                        <input
                            type="radio"
                            id={optionId}
                            name={name}
                            value={option.value}
                            checked={selectedValue === option.value}
                            onChange={() => handleChange(option.value)}
                        />
                        <label htmlFor={optionId} className="radio-group-option-label" >{option.label}</label>
                    </div>
                )})}
            </div>
        </div>
    )
}

const Step2 = ({ onContinue }: { onContinue: () => void }) => {
    let storedStep2Form: any = null;
    const leadSubmittedRef = useRef(false);
    try {
        const raw = localStorage.getItem('scanCheckoutStep2Form');
        storedStep2Form = raw ? JSON.parse(raw) : null;
    } catch {}

    let storedSignupResponse: any = null;
    try {
        const raw = localStorage.getItem('signupResponse');
        storedSignupResponse = raw ? JSON.parse(raw) : null;
    } catch {}

    const storedFirstName = storedStep2Form?.firstName ?? storedSignupResponse?.first_name ?? '';
    const storedLastName = storedStep2Form?.lastName ?? storedSignupResponse?.last_name ?? '';
    const storedEmail = storedStep2Form?.email ?? storedSignupResponse?.email ?? '';
    const storedPhone = storedStep2Form?.phone ?? storedSignupResponse?.phone ?? '';
    const storedDob = storedStep2Form?.dob ?? storedSignupResponse?.dob ?? '';
    const storedGender = storedStep2Form?.gender ?? storedSignupResponse?.gender ?? 'male';
    const storedAcceptTerms = storedStep2Form?.acceptTerms ?? false;

    const storedReasonForScan =
        storedStep2Form?.reasonForScan ??
        "";
    const storedPreviousImaging = storedStep2Form?.previousImaging ?? 'No';
    const storedImplants = storedStep2Form?.implants ?? 'No';
    const storedRecentSurgery = storedStep2Form?.recentSurgery ?? 'No';
    const storedAnyQuestions = storedStep2Form?.anyQuestions ?? '';
    const storedHeardAboutUs = storedStep2Form?.heardAboutUs ?? '';

    const [firstName, setFirstName] = useState(storedFirstName);
    const [lastName, setLastName] = useState(storedLastName);
    const [email, setEmail] = useState(storedEmail);
    const [phone, setPhone] = useState(storedPhone);
    const [dob, setDob] = useState(storedDob);
    const [gender, setGender] = useState(storedGender);
    const [acceptTerms, setAcceptTerms] = useState(storedAcceptTerms);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

  
    const [reasonForScan, setReasonForScan] = useState(storedReasonForScan);
    const [previousImaging, setPreviousImaging] = useState(storedPreviousImaging);
    const [implants, setImplants] = useState(storedImplants);
    const [recentSurgery, setRecentSurgery] = useState(storedRecentSurgery);
    const [anyQuestions, setAnyQuestions] = useState(storedAnyQuestions);
    const [heardAboutUs, setHeardAboutUs] = useState(storedHeardAboutUs);

    // Keep Step 2 values across refresh and back-navigation.
    useEffect(() => {
        const t = setTimeout(() => {
            try {
                localStorage.setItem(
                    'scanCheckoutStep2Form',
                    JSON.stringify({
                        firstName,
                        lastName,
                        email,
                        phone,
                        dob,
                        gender,
                        acceptTerms,
                        reasonForScan,
                        previousImaging,
                        implants,
                        recentSurgery,
                        anyQuestions,
                        heardAboutUs,
                    })
                );
            } catch {}
        }, 200);
        return () => clearTimeout(t);
    }, [
        firstName,
        lastName,
        email,
        phone,
        dob,
        gender,
        acceptTerms,
        reasonForScan,
        previousImaging,
        implants,
        recentSurgery,
        anyQuestions,
        heardAboutUs,
    ]);

    const clearError = (field: string) => {
        if (errors[field]) {
            setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!firstName.trim()) newErrors.firstName = 'First name is required';
        if (!email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!phone.trim()) newErrors.phone = 'Phone number is required';
        if (!dob) {
            newErrors.dob = 'Date of birth is required';
        } else {
            const birthDate = new Date(dob);
            const minDate = new Date();
            minDate.setDate(minDate.getDate() - 5840);
            if (birthDate > minDate) {
                newErrors.dob = 'You must be at least 16 years old';
            }
        }
        if (!gender) newErrors.gender = 'Gender is required';
        if (!acceptTerms) {
            newErrors.acceptTerms = 'Required';
            toast.error('Please accept the terms and conditions');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const baseURL = isProd
                ? process.env.REACT_APP_API_BASE_URL_PROD
                : process.env.REACT_APP_API_BASE_URL_DEV;

            const formattedPhone = phone[0] !== '+' ? '+' + phone : phone;

            // Submit lead to RH before core signup call (once per page load)
            if (!leadSubmittedRef.current) {
                leadSubmittedRef.current = true;
                submitLeadToRhForm({
                    name: `${firstName.trim()} ${lastName.trim()}`.trim(),
                    email: email.trim(),
                    phone: formattedPhone,
                });
            }

            
            let bookingData = null;
            const bookingDataStr = localStorage.getItem('bookingData');
            if (bookingDataStr) {
                try { bookingData = JSON.parse(bookingDataStr); } catch {}
            }

            
            let productId = null;
            const selectedPlanStr = localStorage.getItem('selectedPlan');
            if (selectedPlanStr) {
                try {
                    const selectedPlan = JSON.parse(selectedPlanStr);
                    productId = selectedPlan.productId || selectedPlan.product?.stripe_price_yearly_id || selectedPlan.product?.stripe_price_monthly_id;
                } catch {}
            }

            const signupPayload: any = {
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                email: email.trim(),
                phone: formattedPhone,
                dob: dob,
                gender: gender.toLowerCase(),
                product_id: productId,
                ...(bookingData && {
                    booking: {
                        site_id: bookingData.site_id,
                        desire_date: bookingData.desire_date,
                        location:
                            (bookingData.location || bookingData.site_name || '')?.trim() || null,
                        postcode: (bookingData.postcode || '')?.trim() || null,
                        center_address: (bookingData.center_address || '')?.trim() || null,
                        reason_for_scan: reasonForScan.trim(),
                        any_previous_imaging: previousImaging,
                        any_implants: implants,
                        any_surgery_past_3_month: recentSurgery,
                        any_questions: anyQuestions.trim() || 'None',
                        heard_about_us: heardAboutUs || '',
                    },
                }),
            };

            const response = await axios.post(
                `${baseURL}/${API_V1_PREFIX}members/signup`,
                signupPayload,
                { headers: { 'Content-Type': 'application/json' } }
            );

            
            const signupResponseData = { ...response.data };
            window.dataLayer.push({
                event: 'personal_details_filled',
                member_details: signupResponseData
            });

        
            localStorage.setItem('signupResponse', JSON.stringify(signupResponseData));
            if (signupResponseData.amount != null) {
                localStorage.setItem('subscriptionAmount', signupResponseData.amount.toString());
            }

            setIsSubmitting(false);
            fireCheckoutDetailsSubmitted();
            onContinue();
        } catch (error: any) {
            setIsSubmitting(false);
            if (error?.response?.data?.detail) {
                toast.error(error.response.data.detail);
            } else if (error?.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error('Failed to create account. Please try again.');
            }
        }
    };

    return (
        <div className='inner-container2' >
            <StepHeader
                stepCount="Step 2 of 3"
                title="Personal details"
                stepProgress={66.66}
            />

            <div className="personal-details-wrapper" >
                <div className="horizontal-gap" >
                    <div>
                        <InputField title="First name" type="text" name="firstName" value={firstName} 
                            onChange={(e) => { setFirstName(e.target.value); clearError('firstName'); }} error={errors.firstName} />
                        <InputField title="Last name" type="text" name="lastName" value={lastName} 
                            onChange={(e) => { setLastName(e.target.value); clearError('lastName'); }} error={errors.lastName} />
                        <InputField title="Email address" type="email" name="email" value={email} 
                            onChange={(e) => { setEmail(e.target.value); clearError('email'); }} error={errors.email} />
                        <div className={errors.phone ? 'error' : ''} style={{ marginBottom: '10px' }}>
                            {/* <PhoneInputComp
                                title="Phone number"
                                selectedPhone={phone}
                                onPhoneChange={(p) => { setPhone(p); clearError('phone'); }}
                                placeholder="07XXX XXX XXX"
                            /> */}
                            <div className="input-field-wrapper filled phone-input-wrapper" >
                            <label className="input-field-title" >Phone number</label>
                                <PhoneInputComp
                                    selectedPhone={phone}
                                    onPhoneChange={(p) => { setPhone(p); clearError('phone'); }}
                                    placeholder="+XX XXXX-XXXX"
                                />
                            </div>
                            {errors.phone && <div className="error-text-message">{errors.phone}</div>}
                        </div>

                        
                        <InputField labelTop={true} title="Date of birth" type="date" name="dob" value={dob}
                            onChange={(e) => { setDob(e.target.value); clearError('dob'); }} error={errors.dob}
                            max={(() => { const d = new Date(); d.setDate(d.getDate() - 5840); return d.toISOString().split('T')[0]; })()} />
                        <RadioGroupComponent 
                            title="Gender"
                            required={true}
                            checked="male"
                            name="gender"
                            onChange={(value) => { setGender(value); clearError('gender'); }}
                            options={[
                                { label: "Male", value: "male" },
                                { label: "Female", value: "female" },
                            ]}
                        />
                    </div>
                    <div>
                        <div className="section-top-icon" >
                            <h4>Medical Screening</h4>
                            <div>Required for scan safety</div>
                            <div className="section-tag" >
                            Data management is GDPR compliant
                            </div>
                        </div>
                        <div className="gray-card-wrapper" >
                            <div className="radio-group-title" >What is the reason for your scan?</div>
                            <textarea placeholder="Enter your reason" className="textarea-wrapper" value={reasonForScan} onChange={(e) => setReasonForScan(e.target.value)} />
                        </div>
                        <RadioGroupComponent 
                            title="Have you had any previous imaging?"
                            required={true}
                            checked="No"
                            name="previous-imaging"
                            onChange={(value) => setPreviousImaging(value)}
                            options={[
                                { label: "No", value: "No" },
                                { label: "Yes", value: "Yes" },
                            ]}
                        />
                        <RadioGroupComponent 
                            title="Do you have any implants? (metallic, dental, cardiac, or hearing devices)"
                            required={true}
                            checked="No"
                            name="implants"
                            onChange={(value) => setImplants(value)}
                            options={[
                                { label: "No", value: "No" },
                                { label: "Yes", value: "Yes" },
                            ]}
                        />
                        <RadioGroupComponent 
                            title="Have you had any surgery in the past 3 months?"
                            required={true}
                            checked="No"
                            name="recent-surgery"
                            onChange={(value) => setRecentSurgery(value)}
                            options={[
                                { label: "No", value: "No" },
                                { label: "Yes", value: "Yes" },
                            ]}
                        />
                        <InputField title="Any requests, concerns or questions?" type="text" name="anyQuestions" value={anyQuestions} onChange={(e) => setAnyQuestions(e.target.value)} />
                    </div>
                    <div>
                        <RadioGroupComponent 
                            title="Where did you hear about us?"
                            name="heard-about-us"
                            onChange={(value) => setHeardAboutUs(value)}
                            options={[
                                { label: "Google", value: "Google" },
                                { label: "Instagram", value: "Instagram" },
                                { label: "Podcast", value: "Podcast" },
                                { label: "A friend", value: "A friend" },
                                { label: "Facebook", value: "Facebook" },
                                { label: "TikTok", value: "TikTok" },
                                { label: "PR", value: "PR" },
                                { label: "Other", value: "Other" },
                            ]}
                        />
                    </div>
                    <div className="checkbox-wrapper" >
                        <Checkbox 
                            checked={acceptTerms}
                            onChange={(e) => { setAcceptTerms(e.target.checked); clearError('acceptTerms'); }}
                            id="terms-and-conditions" 
                        />
                        <label htmlFor="terms-and-conditions" className="checkbox-label" >I have read and agree to CoreVital's <a href="https://www.corevitals.me/member-terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms of Use</a> and <a href="https://www.corevitals.me/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>. I furthermore agree to share my personal information with the scanning centre.</label>
                    </div>
                    <button className='black-btn' onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? 'Processing...' : 'Continue to payment'}
                    </button>
                </div>

                <div className="personal-details-wrapper-footer" >We will use any contact details provided to get in touch with you regarding this booking.</div>
            </div>
        </div>
    )
}

export default Step2;
